import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { asRpcError, callRpc, num, numOrNull, useLiveQuery, type ActionResult, type LiveState, type RpcError } from './rpc';

/**
 * agency-billing (design §9, 0166): what the portal reads and asks about the agency's own subscription.
 *
 * - `my_billing_status()`: one row per ACTIVE membership, suspended or not (a DEFINER read, so the paused
 *   screen can use it while the office helpers hide the agency). Money and the pay link only for a payer.
 * - `billing_overview(p_agency)`: Settings > Billing (billing.manage; refused while suspended).
 * - `billing_public_flags()`: the three portal switches. They ship OFF.
 * - `billing_checkout_request` / `billing_portal_request` put a command in the outbox; the billing process
 *   answers it with a Stripe URL that `billing_request_status(p_request)` hands back. The portal polls it,
 *   then leaves for Stripe (`billingRedirect.assign`).
 *
 * Money is integer cents (USD only). Dates are instants, cut in the agency's time zone by the screens.
 */

export const BILLING_STATUSES = ['trialing', 'trial_expired', 'active', 'past_due', 'suspended', 'canceled', 'comp'] as const;
export type BillingAccountStatus = (typeof BILLING_STATUSES)[number];

export const BILLING_INVOICE_STATUSES = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const;
export type BillingInvoiceStatus = (typeof BILLING_INVOICE_STATUSES)[number];

const isStatus = (v: unknown): v is BillingAccountStatus => typeof v === 'string' && (BILLING_STATUSES as readonly string[]).includes(v);
const isInvoiceStatus = (v: unknown): v is BillingInvoiceStatus =>
  typeof v === 'string' && (BILLING_INVOICE_STATUSES as readonly string[]).includes(v);
/** billing-followups (0170, F3): the pay path `my_billing_status()` offers the payer. */
export const BILLING_PAY_PATHS = ['invoice', 'portal', 'checkout'] as const;
export type BillingPayPath = (typeof BILLING_PAY_PATHS)[number];
const isPayPath = (v: unknown): v is BillingPayPath => typeof v === 'string' && (BILLING_PAY_PATHS as readonly string[]).includes(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
/** Only an https URL is ever followed or linked (the server checks too). */
const httpsUrl = (v: unknown): string | null => {
  const s = str(v);
  return s && /^https:\/\//.test(s) ? s : null;
};

/** One row of `my_billing_status()`, camelCased. */
export interface BillingStatus {
  agencyId: string;
  agencyName: string;
  timeZone: string;
  status: BillingAccountStatus;
  suspended: boolean;
  suspendedSourceIsBilling: boolean;
  /** Admin, or a billing.manage override; never under an operator suspension, a comp or a test agency. */
  canPay: boolean;
  /** Cents still owed on the open invoice (payers only; null otherwise). */
  amountDueCents: number | null;
  currency: string | null;
  graceEndsAt: string | null;
  trialEndsAt: string | null;
  /** The open invoice's hosted page (payers only). */
  payUrl: string | null;
  /**
   * How the payer pays (null for everyone else, and when no path is open): the open invoice's page, the
   * Stripe Customer Portal, or a new subscription through Checkout (0170: always open to the payer of a
   * billing-suspended agency with no live subscription).
   */
  payWith: BillingPayPath | null;
}

export function toBillingStatus(r: Record<string, unknown>): BillingStatus | null {
  if (!isStatus(r.status) || typeof r.agency_id !== 'string') return null;
  return {
    agencyId: r.agency_id,
    agencyName: String(r.agency_name ?? ''),
    timeZone: str(r.time_zone) ?? 'America/Chicago',
    status: r.status,
    suspended: r.suspended === true,
    suspendedSourceIsBilling: r.suspended_source_is_billing === true,
    canPay: r.can_pay === true,
    amountDueCents: numOrNull(r.amount_due as number | string | null | undefined),
    currency: str(r.currency),
    graceEndsAt: str(r.grace_ends_at),
    trialEndsAt: str(r.trial_ends_at),
    payUrl: httpsUrl(r.pay_url),
    payWith: isPayPath(r.pay_with) ? r.pay_with : null,
  };
}

export interface BillingFlags {
  selfSignup: boolean;
  portalCheckout: boolean;
  customerPortal: boolean;
}

const FLAGS_OFF: BillingFlags = { selfSignup: false, portalCheckout: false, customerPortal: false };

/**
 * `my_billing_status()` once per user (and on `reload`). `enabled = false` never asks (a role that cannot
 * pay has nothing to show). A failure is logged and reads as no rows: every screen that uses it is a
 * courtesy on top of what already works.
 */
export function useMyBillingStatus(enabled = true): { rows: BillingStatus[]; loaded: boolean; reload: () => void } {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  const [state, setState] = React.useState<{ rows: BillingStatus[]; loaded: boolean }>({ rows: [], loaded: false });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  React.useEffect(() => {
    if (!enabled || !userId) {
      setState({ rows: [], loaded: false });
      return;
    }
    let active = true;
    void (async () => {
      const { data, error } = await client.rpc('my_billing_status');
      if (!active) return;
      if (error) {
        console.warn('useMyBillingStatus: my_billing_status failed', error);
        setState({ rows: [], loaded: true });
        return;
      }
      const rows = ((data ?? []) as Record<string, unknown>[]).map(toBillingStatus).filter((r): r is BillingStatus => r !== null);
      setState({ rows, loaded: true });
    })();
    return () => {
      active = false;
    };
  }, [client, userId, enabled, nonce]);
  return { ...state, reload };
}

/** `billing_public_flags()`: all OFF until it answers, and on any failure. */
export function useBillingFlags(): BillingFlags {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  const [flags, setFlags] = React.useState<BillingFlags>(FLAGS_OFF);
  React.useEffect(() => {
    if (!userId) return;
    let active = true;
    void (async () => {
      const { data, error } = await client.rpc('billing_public_flags');
      if (!active) return;
      if (error) {
        console.warn('useBillingFlags: billing_public_flags failed', error);
        setFlags(FLAGS_OFF);
        return;
      }
      const r = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined;
      setFlags({
        selfSignup: r?.self_signup_enabled === true,
        portalCheckout: r?.portal_checkout_enabled === true,
        customerPortal: r?.customer_portal_enabled === true,
      });
    })();
    return () => {
      active = false;
    };
  }, [client, userId]);
  return flags;
}

export interface BillingInvoice {
  number: string | null;
  status: BillingInvoiceStatus;
  periodStart: string | null;
  periodEnd: string | null;
  totalCents: number;
  amountRemainingCents: number;
  hostedInvoiceUrl: string | null;
  paidAt: string | null;
  createdAt: string | null;
}

export interface BillingOverview {
  agencyId: string;
  timeZone: string;
  status: BillingAccountStatus;
  arrangement: 'standard' | 'enterprise' | 'comp';
  /** The band billed now (null before the first subscription). */
  plan: { key: string; name: string } | null;
  /** The band of this period's peak: what the next renewal bills. */
  band: { key: string; name: string; minTrucks: number | null; maxTrucks: number | null } | null;
  trucksNow: number;
  /** The most active trucks the plan allows (0175); null = no cap (enterprise, comp, test). */
  truckCap: number | null;
  trucksPeriodPeak: number;
  priceCents: number | null;
  nextPriceCents: number | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  /** The trial's length in days (`trial_days_total`), null when not on trial or from an older server. */
  trialDaysTotal: number | null;
  graceEndsAt: string | null;
  pendingPriceChange: boolean;
  pendingEffectiveAfter: string | null;
  hasSubscription: boolean;
  hasCustomer: boolean;
  portalCheckoutEnabled: boolean;
  customerPortalEnabled: boolean;
  invoices: BillingInvoice[];
}

type Json = Record<string, unknown>;

export function toBillingOverview(r: Json): BillingOverview | null {
  if (!r || typeof r !== 'object' || !isStatus(r.status)) return null;
  const plan = r.plan as Json | null;
  const band = r.band as Json | null;
  const arrangement = r.arrangement === 'enterprise' || r.arrangement === 'comp' ? r.arrangement : 'standard';
  return {
    agencyId: String(r.agency_id ?? ''),
    timeZone: str(r.time_zone) ?? 'America/Chicago',
    status: r.status,
    arrangement,
    plan: plan && typeof plan === 'object' ? { key: String(plan.key ?? ''), name: String(plan.name ?? '') } : null,
    band:
      band && typeof band === 'object'
        ? {
            key: String(band.key ?? ''),
            name: String(band.name ?? ''),
            minTrucks: numOrNull(band.min_trucks as number | null),
            maxTrucks: numOrNull(band.max_trucks as number | null),
          }
        : null,
    trucksNow: num(r.trucks_now as number | null),
    truckCap: numOrNull(r.truck_cap as number | null),
    trucksPeriodPeak: num(r.trucks_period_peak as number | null),
    priceCents: numOrNull(r.price_cents as number | null),
    nextPriceCents: numOrNull(r.next_price_cents as number | null),
    currentPeriodEnd: str(r.current_period_end),
    cancelAtPeriodEnd: r.cancel_at_period_end === true,
    trialEndsAt: str(r.trial_ends_at),
    trialDaysLeft: numOrNull(r.trial_days_left as number | null),
    trialDaysTotal: numOrNull((r.trial_days_total ?? null) as number | null),
    graceEndsAt: str(r.grace_ends_at),
    pendingPriceChange: r.pending_price_change === true,
    pendingEffectiveAfter: str(r.pending_effective_after),
    hasSubscription: r.has_subscription === true,
    hasCustomer: r.has_customer === true,
    portalCheckoutEnabled: r.portal_checkout_enabled === true,
    customerPortalEnabled: r.customer_portal_enabled === true,
    invoices: (Array.isArray(r.invoices) ? (r.invoices as Json[]) : [])
      .filter((i) => isInvoiceStatus(i.status))
      .map((i) => ({
        number: str(i.number),
        status: i.status as BillingInvoiceStatus,
        periodStart: str(i.period_start),
        periodEnd: str(i.period_end),
        totalCents: num(i.total as number | null),
        amountRemainingCents: num(i.amount_remaining as number | null),
        hostedInvoiceUrl: httpsUrl(i.hosted_invoice_url),
        paidAt: str(i.paid_at),
        createdAt: str(i.created_at),
      })),
  };
}

/** Settings > Billing. `enabled = false` (no billing.manage) never asks: the RPC would refuse it. */
export function useBillingOverview(enabled: boolean): LiveState<BillingOverview | null> {
  return useLiveQuery<BillingOverview | null>(
    async (client, agencyId) => {
      if (!enabled) return { data: null };
      const { data, error } = await client.rpc('billing_overview', { p_agency: agencyId });
      if (error) return { error: asRpcError(error)! };
      return { data: toBillingOverview((Array.isArray(data) ? data[0] : data) as Json) };
    },
    [enabled],
  );
}

/**
 * Where the portal sends the browser for a Stripe page, and how it waits for one. An object, so a test can
 * replace `assign` (jsdom cannot navigate) and shorten the wait.
 */
export const billingRedirect = {
  assign(url: string): void {
    window.location.assign(url);
  },
  /** Between two `billing_request_status` reads. */
  pollMs: 1000,
  /** Reads before giving up (about a minute). */
  maxPolls: 60,
};

export type BillingRequestKind = 'checkout' | 'portal';

const REQUEST_RPC: Record<BillingRequestKind, string> = {
  checkout: 'billing_checkout_request',
  portal: 'billing_portal_request',
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Ask for a Stripe page for `agencyId`, wait for the billing process to answer, then leave for it. Resolves
 * with an error when the request is refused, fails, or gets no answer in time (`billing:failed`,
 * `billing:timeout`: describeRpcError maps both). On success the page is already leaving.
 */
export async function requestBillingPage(
  client: ReturnType<typeof useAuth>['client'],
  kind: BillingRequestKind,
  agencyId: string,
): Promise<ActionResult<string>> {
  const req = await callRpc<string>(client, REQUEST_RPC[kind], { p_agency: agencyId });
  if (req.error) return { data: null, error: req.error };
  const id = typeof req.data === 'string' ? req.data : null;
  if (!id) return { data: null, error: { code: 'billing:failed', message: 'no request id' } };
  for (let i = 0; i < billingRedirect.maxPolls; i += 1) {
    if (i > 0) await sleep(billingRedirect.pollMs);
    const res = await callRpc<Json[] | Json>(client, 'billing_request_status', { p_request: id });
    if (res.error) return { data: null, error: res.error };
    const row = (Array.isArray(res.data) ? res.data[0] : res.data) as Json | null | undefined;
    if (!row || row.status === 'failed') return { data: null, error: { code: 'billing:failed', message: 'request failed' } };
    const url = row.status === 'done' ? httpsUrl(row.url) : null;
    if (row.status === 'done' && !url) return { data: null, error: { code: 'billing:failed', message: 'no url' } };
    if (url) {
      billingRedirect.assign(url);
      return { data: url, error: null };
    }
  }
  return { data: null, error: { code: 'billing:timeout', message: 'no answer' } };
}

/** The pay / subscribe / manage buttons: one request at a time, its error translated by the caller. */
export function useBillingRequest(): {
  busy: boolean;
  error: RpcError | null;
  request: (kind: BillingRequestKind, agencyId: string) => Promise<ActionResult<string>>;
} {
  const { client } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<RpcError | null>(null);
  const busyRef = React.useRef(false);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const request = React.useCallback(
    async (kind: BillingRequestKind, agencyId: string): Promise<ActionResult<string>> => {
      if (busyRef.current) return { data: null, error: { code: 'busy', message: 'another action is running' } };
      busyRef.current = true;
      setBusy(true);
      setError(null);
      try {
        const res = await requestBillingPage(client, kind, agencyId);
        if (mounted.current) setError(res.error);
        return res;
      } finally {
        busyRef.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [client],
  );
  return { busy, error, request };
}
